import React, { useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import { useDetachedState } from '@/components/hooks/use-detached-state';
import { FileTileModel } from '@/features/ai-agents/shared/models';
import { useMediaApi } from '@/webapi/use-media-api';

const MAX_FILES = 3;
const MAX_SIZE = 5 * 1024 * 1024; // 5MB

export function useChatFilesUpload() {
  const [filesToUpload, setFilesToUpload] = React.useState<File[]>([]);
  const [uploadedFiles, setUploadedFiles] = React.useState<string[]>([]);
  const [uploadedFilesRef, setUploadedFilesRef] = useState<File[]>([]);
  const [isUploading, setIsUploading, isUploadingRef] = useDetachedState(false);

  const { uploadGenericImage } = useMediaApi();

  const dropzoneProps = useDropzone({
    maxFiles: MAX_FILES,
    maxSize: MAX_SIZE,
    onDropAccepted: (acceptedFiles) => {
      setFilesToUpload((prevFiles) => [...prevFiles, ...acceptedFiles]);
    },
    onDropRejected: (_) => {
      toast(
        `File upload failed: Please upload an image file no larger than 5MB`,
        {
          theme: `colored`,
          type: `error`,
        },
      );
    },
  });

  useEffect(() => {
    if (filesToUpload.length > 0 && !isUploadingRef?.current) {
      const current = filesToUpload[0];
      setIsUploading(true);

      uploadFile(current).then((url) => {
        setUploadedFiles((prevFiles) => [url, ...prevFiles]);
        setFilesToUpload((prevFiles) => prevFiles.filter((f) => f !== current));
        setUploadedFilesRef((prevFiles) => [current, ...prevFiles]);
        setIsUploading(false);
      });
    }
  }, [filesToUpload]);

  const onImageDelete = (url: string) => {
    setUploadedFiles((prevFiles) => prevFiles.filter((f) => f !== url));
  };

  const uploadFile = async (file: File): Promise<string> => {
    const resp = await uploadGenericImage(file);
    return resp.url;
  };

  const clearFiles = () => {
    setFilesToUpload([]);
    setUploadedFiles([]);
    setUploadedFilesRef([]);
  };

  const filesList: FileTileModel[] = useMemo(() => {
    const out = [];
    filesToUpload.forEach((file) => {
      out.push({
        file,
        url: URL.createObjectURL(file),
        isUploading: true,
      });
    });

    uploadedFiles.forEach((file) => {
      out.push({
        url: file,
        isUploading: false,
      });
    });
    return out;
  }, [filesToUpload, uploadedFiles]);

  const reachMaxFiles = useMemo(
    () => filesList.length === MAX_FILES,
    [filesList.length],
  );

  const addFileToUploadQueue = (file: File) => {
    setFilesToUpload((prevFiles) => [...prevFiles, file]);
  };

  return {
    filesList,
    uploadedFiles,
    onImageDelete,
    dropzoneProps,
    isUploading,
    reachMaxFiles,
    uploadedFilesRef,
    clearFiles,
    addFileToUploadQueue,
  };
}
